import React, { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../../App";
import ChangePasswordModal from "../../components/User/ChangePasswordModal";
import EditSupplierAccountModal from "../../components/User/EditSupplierAccountModal";
import AccountModal from "../../components/User/AccountModal";
import Table from "react-bootstrap/Table";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import { getLabUsers } from "../../clients/user_client";
import {
  getAccountRole,
  getAccountRoleLabel,
  isLabAdmin,
  isLabViewer,
} from "../../utils/userAccountRole";
import { showToast } from "../../config_and_helpers/helpers";
import { logInfo, logError } from "../../config_and_helpers/logger";
import GlassWorkspaceShell from "../../components/Glass/GlassWorkspaceShell";
import "./UserPageStyle.css";

const LOG_CONTEXT = "AccountPage";

/**
 * Single label/value row for account profile panels.
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {React.ReactNode} props.value
 * @param {boolean} [props.emphasis=false] - larger label typography
 */
const AccountDetailRow = ({ label, value, emphasis = false }) => (
  <div
    className={`account-page__row${emphasis ? " account-page__row--emphasis" : ""}`}
  >
    <dt className="account-page__label">{label}</dt>
    <dd className="account-page__value">{value}</dd>
  </div>
);

/**
 * Account details for the logged-in user.
 * Lab Admins also see a Lab users directory (email / phone / role).
 */
const Account = () => {
  const { token, userDetails, isSupplier } = useContext(AppContext);
  const labAdmin = isLabAdmin(userDetails, isSupplier);
  const labViewer = isLabViewer(userDetails, isSupplier);
  const roleKind = labViewer ? "viewer" : labAdmin ? "admin" : "staff";
  const [labUsers, setLabUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadLabUsers = useCallback(async () => {
    if (!token || !labAdmin) {
      return;
    }
    setLoadingUsers(true);
    const result = await getLabUsers(token);
    setLoadingUsers(false);
    if (!result.success) {
      logError(LOG_CONTEXT, "getLabUsers failed", {
        status: result.status ?? null,
        error: result.error,
      });
      showToast(
        result.status === 403
          ? "Not allowed to list lab users."
          : "Failed to load lab users.",
        "error",
        "top-right",
        4000,
      );
      setLabUsers([]);
      return;
    }
    setLabUsers(result.users || []);
    logInfo(LOG_CONTEXT, "lab users loaded", { count: result.users?.length });
  }, [token, labAdmin]);

  useEffect(() => {
    loadLabUsers();
  }, [loadLabUsers]);

  const roleLabel = getAccountRoleLabel(userDetails, isSupplier);

  return (
    <GlassWorkspaceShell theme={isSupplier ? "rose" : "yellow"} wide={labAdmin}>
      <div className={`account-page${labAdmin ? " account-page--wide" : ""}`}>
        <div className="glass-panel account-page__profile">
          <div className="account-page__header">
            <h1 className="glass-page-heading mb-0">Account Details</h1>
            {!isSupplier ? (
              <Badge
                className={`account-page__role-badge account-page__role-badge--${roleKind}`}
              >
                {roleLabel}
              </Badge>
            ) : null}
          </div>

          <dl className="account-page__details">
            <AccountDetailRow
              label="Username"
              value={userDetails.username}
              emphasis
            />
            <AccountDetailRow
              label="Name"
              value={`${userDetails.first_name} ${userDetails.last_name}`}
            />
            <AccountDetailRow label="Email" value={userDetails.email} />
            <AccountDetailRow
              label="Phone"
              value={`${userDetails.phone_prefix}-${userDetails.phone_suffix}`}
            />
          </dl>

          {isSupplier ? (
            <>
              <h2 className="account-page__section-title">Supplier Details</h2>
              <dl className="account-page__details">
                <AccountDetailRow
                  label="Name"
                  value={userDetails.supplier_name}
                  emphasis
                />
                <AccountDetailRow
                  label="Quote Email"
                  value={userDetails.supplier_email}
                />
                <AccountDetailRow
                  label="Office Phone"
                  value={`${userDetails.supplier_phone_prefix}-${userDetails.supplier_phone_suffix}`}
                />
                <AccountDetailRow
                  label="Website"
                  value={userDetails.supplier_website || "—"}
                />
              </dl>
            </>
          ) : null}

          <div className="account-page__actions">
            {isSupplier ? (
              <EditSupplierAccountModal />
            ) : labViewer ? (
              <p className="glass-page-sub mb-0">
                Ask a lab Admin to change your email or phone. You can still
                reset your password by email.
              </p>
            ) : (
              <AccountModal
                canEditRole={labAdmin}
                onSaved={() => {
                  if (labAdmin) {
                    loadLabUsers();
                  }
                }}
              />
            )}
            <ChangePasswordModal
              userEmail={userDetails.email}
              buttonText="Change Password"
            />
          </div>
        </div>

        {labAdmin ? (
          <div className="glass-panel account-page__directory">
            <div className="account-page__directory-header">
              <div>
                <h2 className="account-page__section-title mb-2">Lab users</h2>
                <p className="glass-page-sub mb-0">
                  Admins can add and edit any lab user&apos;s email, phone, and
                  account role. Staff see only their own account on this page.
                </p>
              </div>
              <AccountModal
                adminCreateUser
                canEditRole
                buttonText="Add user"
                buttonVariant="success"
                onSaved={() => loadLabUsers()}
              />
            </div>
            {loadingUsers ? (
              <div className="account-page__loading py-3">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading users…
              </div>
            ) : (
              <div className="glass-table-wrap">
                <Table hover responsive size="sm" className="account-page__table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {!labUsers.length ? (
                      <tr>
                        <td colSpan={6} className="account-page__empty">
                          No lab users found.
                        </td>
                      </tr>
                    ) : (
                      labUsers.map(row => {
                        const rowId = row.user_id ?? row.id;
                        return (
                          <tr key={rowId}>
                            <td>{row.username}</td>
                            <td>
                              {[row.first_name, row.last_name]
                                .filter(Boolean)
                                .join(" ") || "—"}
                            </td>
                            <td>{row.email || "—"}</td>
                            <td>
                              {row.phone_prefix && row.phone_suffix
                                ? `${row.phone_prefix}-${row.phone_suffix}`
                                : "—"}
                            </td>
                            <td>
                              <Badge
                                className={`account-page__role-badge account-page__role-badge--${
                                  getAccountRole(row) || "staff"
                                }`}
                              >
                                {getAccountRoleLabel(row)}
                              </Badge>
                            </td>
                            <td className="account-page__table-actions">
                              <AccountModal
                                targetUser={row}
                                canEditRole
                                buttonText="Edit"
                                buttonVariant="outline-primary"
                                buttonSize="sm"
                                onSaved={() => loadLabUsers()}
                              />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </GlassWorkspaceShell>
  );
};
export default Account;
